import { ApiError } from '@holdthat/api';
import type { GroupKeyGrantV1, KeyEnvelopeV1, PublicKeysV1 } from '@holdthat/crypto';

export class WebRequestError extends ApiError {
  readonly method: string;
  readonly path: string;
  constructor(status: number, method: string, path: string, code?: string) {
    // No query strings, request bodies, tokens or submitted content in diagnostics.
    const safePath = path.split(/[?#]/, 1)[0]!;
    const explanation = code === 'invalid_session_origin' ? ' · Der Server erlaubt diese Webadresse nicht für Cookie-Schreibzugriffe. Bitte PUBLIC_BASE_URL und WEB_SESSION_ORIGINS der Serverkonfiguration prüfen. Vorgemerkte Inhalte bleiben unter dieser Webadresse erhalten; nicht die Domain wechseln oder Browserdaten löschen.' : '';
    super(status, `HTTP ${status} · ${method} ${safePath}${code ? ` · ${code}` : ''}${explanation}`, code);
    this.method = method; this.path = safePath;
  }
}

/** Web-only transport. Never cache authenticated product responses. */
export async function webRequest<T>(path: string, token?: string, options: { method?: string; body?: unknown; headers?: Record<string, string>; binary?: Blob; responseBytes?: boolean; signal?: AbortSignal } = {}): Promise<T> {
  const headers = new Headers(options.headers); headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (options.body !== undefined) headers.set('Content-Type', 'application/json');
  const deadline = AbortSignal.timeout(options.binary || options.responseBytes ? 120_000 : 30_000);
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ''}${path}`, { method: options.method, headers, body: options.binary ?? (options.body === undefined ? undefined : JSON.stringify(options.body)), credentials: 'same-origin', cache: 'no-store', signal: options.signal ? AbortSignal.any([options.signal, deadline]) : deadline });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => undefined);
    const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : undefined;
    // Match the server's exact, non-sensitive legacy CSRF diagnostic. Never
    // echo arbitrary response strings (which can contain submitted content).
    const code = response.status === 403 && detail === 'Invalid session origin' ? 'invalid_session_origin' : detail && typeof detail === 'object' && 'code' in detail && typeof detail.code === 'string' ? detail.code : undefined;
    throw new WebRequestError(response.status, options.method ?? 'GET', path, code);
  }
  return response.status === 204 ? undefined as T : options.responseBytes ? response.arrayBuffer() as Promise<T> : response.json() as Promise<T>;
}
export interface RotationInventory {
  groupID: string; previousEpoch: number; grant: GroupKeyGrantV1;
  members: { userID: string; publicKeys?: PublicKeysV1 | null }[];
  envelopes: KeyEnvelopeV1[];
}
export function assertCursorProgress(previous: number, next: number, hasMore: boolean) {
  if (!Number.isSafeInteger(next) || next < previous || (hasMore && next <= previous)) throw new Error('Ungültiger Sync-Cursor. Der Abgleich wurde ohne Datenverlust angehalten.');
}
